import type { Provider } from '@/types/provider'
import type { QueueResponse } from '@/types/queue'
import type { CaseReviewBundle } from '@/types/visit'
import type {
  RecordingListResponse,
  NoteListResponse,
  CodeSuggestion,
  TreatmentPlan,
  CompleteVisitPayload,
} from '@/types/documentation'
import type { PatientSearchResult, PatientRecord } from '@/types/patient'
import type { ProviderProfile, PracticeProfile, VisitType } from '@/types/settings'
import type { Availability } from '@/types/availability'
import type { OnboardingProgress, ProviderType } from '@/types/onboarding'
import type { StripeAccountStatus } from '@/schemas/onboarding-stripe'
import type { RevenueSummary, MonthlyRevenueRow } from '@/types/payments'
import { config } from './config'

export interface RegisterProviderData {
  name: string
  email: string
  password: string
  provider_type: ProviderType
}

const API_BASE_URL = config.apiBaseUrl

// API error response types
interface ValidationError {
  type: string
  loc: (string | number)[]
  msg: string
  input?: unknown
  ctx?: Record<string, unknown>
}

interface ErrorResponse {
  detail?: string | ValidationError[] | { message?: string }
  message?: string
}

export class ApiError extends Error {
  constructor(public status: number, message: string) {
    super(message)
    this.name = 'ApiError'
  }
}

export class ProviderPortalApiClient {
  private baseUrl: string
  private onAuthError?: () => void
  private isRefreshing: boolean = false
  private failedQueue: Array<{
    resolve: (value: string) => void
    reject: (error: unknown) => void
  }> = []

  constructor(baseUrl: string = API_BASE_URL) {
    this.baseUrl = baseUrl
  }

  setAuthErrorHandler(handler: () => void) {
    this.onAuthError = handler
  }

  private processQueue(error: unknown, token: string | null = null): void {
    this.failedQueue.forEach(({ resolve, reject }) => {
      if (error) {
        reject(error)
      } else if (token) {
        resolve(token)
      }
    })
    this.failedQueue = []
  }

  private queueFailedRequest(): Promise<string> {
    return new Promise<string>((resolve, reject) => {
      this.failedQueue.push({ resolve, reject })
    })
  }

  private async executeTokenRefresh(): Promise<string> {
    await this.refreshToken()
    return 'refreshed'
  }

  private handleRefreshFailure(error: unknown): void {
    this.processQueue(error, null)
    // Don't trigger the global auth-error handler here. In dev, MSW
    // intercepts `/auth/refresh` and always returns 401 against
    // real-backend cookies, which would force a logout on every
    // periodic refresh. If a refresh genuinely fails in prod, the next
    // API call's 401 will trigger the logout via the regular request
    // path — losing one extra round-trip is the right trade-off.
  }

  private async handleTokenRefresh(): Promise<string> {
    if (this.isRefreshing) {
      return this.queueFailedRequest()
    }

    this.isRefreshing = true

    try {
      const result = await this.executeTokenRefresh()
      this.processQueue(null, result)
      return result
    } catch (error) {
      this.handleRefreshFailure(error)
      throw error
    } finally {
      this.isRefreshing = false
    }
  }

  private shouldAttemptRefresh(endpoint: string, status: number, isRetry: boolean): boolean {
    return status === 401 && !isRetry && !endpoint.includes('/auth/')
  }

  private buildRequestConfig(options: RequestInit): RequestInit {
    const { headers: optionHeaders, ...rest } = options
    const isFormData = options.body instanceof FormData
    return {
      headers: {
        ...(isFormData ? {} : { 'Content-Type': 'application/json' }),
        ...(optionHeaders as Record<string, string>),
      },
      credentials: 'include',
      ...rest,
    }
  }

  private extractErrorMessage(errorData: ErrorResponse): string {
    if (typeof errorData.detail === 'string') {
      return errorData.detail
    } else if (Array.isArray(errorData.detail)) {
      return errorData.detail.map((err) => err.msg || JSON.stringify(err)).join(', ')
    } else if (errorData.detail && typeof errorData.detail === 'object') {
      return errorData.detail.message || JSON.stringify(errorData.detail)
    } else if (errorData.message) {
      return errorData.message
    }
    return 'Unknown error'
  }

  private async request<T>(
    endpoint: string,
    options: RequestInit = {},
    isRetry: boolean = false
  ): Promise<T> {
    const url = `${this.baseUrl}${endpoint}`
    const requestConfig = this.buildRequestConfig(options)

    const response = await fetch(url, requestConfig)

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({ detail: 'Unknown error' }))

      if (this.shouldAttemptRefresh(endpoint, response.status, isRetry)) {
        try {
          await this.handleTokenRefresh()
          return this.request<T>(endpoint, options, true)
        } catch (_refreshError) {
          throw new ApiError(response.status, this.extractErrorMessage(errorData))
        }
      }

      if (response.status === 401) {
        // `/provider/me` 401s are benign on initial mount: with no
        // session yet, the real backend correctly returns 401, but we
        // don't want it to short-circuit the boot flow into a forced
        // /login. The auth context handles its own hydration via
        // localStorage and decides whether to clear state.
        if (this.onAuthError && !endpoint.endsWith('/api/v1/provider/me')) {
          this.onAuthError()
        }
        throw new ApiError(response.status, this.extractErrorMessage(errorData))
      }

      throw new ApiError(response.status, this.extractErrorMessage(errorData))
    }

    if (response.status === 204 || response.headers.get('content-length') === '0') {
      return undefined as T
    }

    return response.json()
  }

  // Auth endpoints
  async login(emailOrUsername: string, password: string): Promise<void> {
    await this.request<void>('/api/v1/auth/login', {
      method: 'POST',
      body: JSON.stringify({ email_or_username: emailOrUsername, password }),
    })
  }

  async getCurrentUser(): Promise<Provider> {
    // Telehealth-specific identity endpoint — returns Provider shape
    // (provider_onboarding_completed, provider_type, practice_id) which
    // the dictation `/auth/me` doesn't carry.
    return this.request<Provider>('/api/v1/provider/me')
  }

  async refreshToken(): Promise<void> {
    await this.request<void>('/api/v1/auth/refresh', {
      method: 'POST',
    })
  }

  async logout(): Promise<void> {
    await this.request<void>('/api/v1/auth/logout', {
      method: 'POST',
    })
  }

  async register(data: RegisterProviderData): Promise<Provider> {
    // Backend creates User + Organization + Practice + ProviderProfile
    // atomically, issues access/refresh cookies, and returns a complete
    // `Provider` so the auth context can hydrate without a follow-up
    // `/auth/me` round-trip. Auth lives in httpOnly cookies — the response
    // body intentionally carries no token fields so persisting the
    // Provider to localStorage doesn't leak refreshable credentials.
    return this.request<Provider>(
      '/api/v1/provider/onboarding/start',
      {
        method: 'POST',
        body: JSON.stringify(data),
      },
    )
  }

  // Onboarding endpoints
  async getOnboardingProgress(): Promise<OnboardingProgress> {
    return this.request<OnboardingProgress>('/api/v1/provider/onboarding')
  }

  async updateOnboardingProgress(data: OnboardingProgress): Promise<OnboardingProgress> {
    return this.request<OnboardingProgress>('/api/v1/provider/onboarding', {
      method: 'PUT',
      body: JSON.stringify(data),
    })
  }

  async completeOnboarding(): Promise<Provider> {
    return this.request<Provider>('/api/v1/provider/onboarding/complete', {
      method: 'POST',
    })
  }

  async getStripeAccountStatus(): Promise<{ status: StripeAccountStatus }> {
    return this.request<{ status: StripeAccountStatus }>(
      '/api/v1/provider/stripe/account-status',
    )
  }

  async createStripeAccountLink(returnUrl?: string): Promise<{ url: string }> {
    return this.request<{ url: string }>(
      '/api/v1/provider/stripe/create-account-link',
      {
        method: 'POST',
        ...(returnUrl && { body: JSON.stringify({ return_url: returnUrl }) }),
      },
    )
  }

  async checkPracticeSlug(slug: string): Promise<{ available: boolean }> {
    return this.request<{ available: boolean }>(
      `/api/v1/provider/practice/slug-available?slug=${encodeURIComponent(slug)}`,
    )
  }

  async uploadProviderHeadshot(file: File): Promise<{ url: string }> {
    const formData = new FormData()
    formData.append('file', file)
    return this.request<{ url: string }>(
      '/api/v1/provider/profile/headshot',
      { method: 'POST', body: formData },
    )
  }

  // Payments endpoints
  async getRevenueSummary(): Promise<RevenueSummary> {
    return this.request<RevenueSummary>('/api/v1/provider/payments/revenue-summary')
  }

  async getRevenueHistory(months: number = 12): Promise<MonthlyRevenueRow[]> {
    return this.request<MonthlyRevenueRow[]>(
      `/api/v1/provider/payments/revenue-history?months=${months}`,
    )
  }

  // Queue endpoints
  async getQueue(): Promise<QueueResponse> {
    return this.request<QueueResponse>('/api/v1/provider/queue')
  }

  // Visit endpoints
  async getVisitDetail(visitId: string): Promise<CaseReviewBundle> {
    return this.request<CaseReviewBundle>(`/api/v1/provider/visits/${visitId}`)
  }

  // Recording endpoints
  async getRecordings(params?: {
    status?: string
    page?: number
    per_page?: number
  }): Promise<RecordingListResponse> {
    const searchParams = new URLSearchParams()
    if (params?.status) searchParams.set('status', params.status)
    if (params?.page) searchParams.set('page', String(params.page))
    if (params?.per_page) searchParams.set('per_page', String(params.per_page))
    const qs = searchParams.toString()
    return this.request<RecordingListResponse>(
      `/api/v1/recordings/${qs ? `?${qs}` : ''}`,
    )
  }

  async getRecordingNotes(recordingId: string): Promise<NoteListResponse> {
    return this.request<NoteListResponse>(
      `/api/v1/recordings/${recordingId}/notes`,
    )
  }

  // Documentation endpoints
  async suggestCodes(
    visitId: string,
    noteContent: string,
  ): Promise<CodeSuggestion> {
    return this.request<CodeSuggestion>(
      `/api/v1/provider/visits/${visitId}/suggest-codes`,
      {
        method: 'POST',
        body: JSON.stringify({ note_content: noteContent }),
      },
    )
  }

  async generateTreatmentPlan(
    visitId: string,
    noteContent: string,
  ): Promise<TreatmentPlan> {
    return this.request<TreatmentPlan>(
      `/api/v1/provider/visits/${visitId}/generate-treatment-plan`,
      {
        method: 'POST',
        body: JSON.stringify({ note_content: noteContent }),
      },
    )
  }

  async completeVisit(
    visitId: string,
    payload: CompleteVisitPayload,
  ): Promise<void> {
    return this.request<void>(
      `/api/v1/provider/visits/${visitId}/complete`,
      {
        method: 'POST',
        body: JSON.stringify(payload),
      },
    )
  }

  // Patient endpoints
  async listPatients(): Promise<PatientSearchResult[]> {
    return this.request<PatientSearchResult[]>('/api/v1/provider/patients')
  }

  async searchPatients(query: string): Promise<PatientSearchResult[]> {
    return this.request<PatientSearchResult[]>(
      `/api/v1/provider/patients/search?q=${encodeURIComponent(query)}`,
    )
  }

  async getPatientRecord(patientId: string): Promise<PatientRecord> {
    return this.request<PatientRecord>(`/api/v1/provider/patients/${patientId}`)
  }

  // Settings endpoints
  async getProviderProfile(): Promise<ProviderProfile> {
    return this.request<ProviderProfile>('/api/v1/provider/profile')
  }

  async updateProviderProfile(data: ProviderProfile): Promise<ProviderProfile> {
    return this.request<ProviderProfile>('/api/v1/provider/profile', {
      method: 'PUT',
      body: JSON.stringify(data),
    })
  }

  async getPracticeProfile(): Promise<PracticeProfile> {
    return this.request<PracticeProfile>('/api/v1/provider/practice')
  }

  async updatePracticeProfile(data: PracticeProfile): Promise<PracticeProfile> {
    return this.request<PracticeProfile>('/api/v1/provider/practice', {
      method: 'PUT',
      body: JSON.stringify(data),
    })
  }

  async getVisitTypes(): Promise<VisitType[]> {
    return this.request<VisitType[]>('/api/v1/provider/visit-types')
  }

  async createVisitType(data: Omit<VisitType, 'id'>): Promise<VisitType> {
    return this.request<VisitType>('/api/v1/provider/visit-types', {
      method: 'POST',
      body: JSON.stringify(data),
    })
  }

  async updateVisitType(id: string, data: Partial<VisitType>): Promise<VisitType> {
    return this.request<VisitType>(`/api/v1/provider/visit-types/${id}`, {
      method: 'PUT',
      body: JSON.stringify(data),
    })
  }

  async getAvailability(): Promise<Availability> {
    return this.request<Availability>('/api/v1/provider/availability')
  }

  async updateAvailability(data: Availability): Promise<Availability> {
    return this.request<Availability>('/api/v1/provider/availability', {
      method: 'PUT',
      body: JSON.stringify(data),
    })
  }
}

export const providerApiClient = new ProviderPortalApiClient()
